import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  Divider,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Save, Close } from "@mui/icons-material";
import {
  usePrescriptionTemplates,
  type VetProtocolQuestionnaireItem,
} from "@/fluxvet/hooks/usePrescriptionTemplates";

// =============================================================================
// TIPOS
// =============================================================================

interface TemplateSaverProps {
  open: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void; // Callback chamado quando salvamento é bem-sucedido
  // Dados atuais do formulário para salvar
  treatments?: Array<{
    id: string;
    name: string;
    dosage: string;
    description: string;
  }>;
  examsOrProcedures?: Array<{ id: string; description: string; notes: string }>;
  reports?: Array<{ id: string; content: string }>;
  guidelines?: string;
  /** Questionários marcados na etapa de prescrições (opcional). */
  questionnaires?: VetProtocolQuestionnaireItem[];
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Modal para salvar prescrições atuais como protocolo reutilizável
 * Permite ao usuário:
 * - Nomear o protocolo
 * - Adicionar descrição opcional
 * - Selecionar quais itens incluir no protocolo (checkboxes)
 */
export const TemplateSaver: React.FC<TemplateSaverProps> = ({
  open,
  onClose,
  onSaveSuccess,
  treatments = [],
  examsOrProcedures = [],
  reports = [],
  guidelines = "",
  questionnaires = [],
}) => {
  const { createTemplate, isLoading: isLoadingTemplates } =
    usePrescriptionTemplates();

  // Estado do formulário
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Estado de seleção de itens (todos selecionados por padrão)
  const [selectedTreatments, setSelectedTreatments] = useState<Set<string>>(
    new Set(treatments.map((t) => t.id))
  );
  const [selectedExams, setSelectedExams] = useState<Set<string>>(
    new Set(examsOrProcedures.map((e) => e.id))
  );
  const [selectedReports, setSelectedReports] = useState<Set<string>>(
    new Set(reports.map((r) => r.id))
  );
  const [includeGuidelines, setIncludeGuidelines] = useState(!!guidelines);
  const [selectedQuestionnaireIds, setSelectedQuestionnaireIds] = useState<
    Set<string>
  >(new Set(questionnaires.map((q) => q.questionnaireId)));

  // Reseta seleções quando os dados mudam (modal abre)
  React.useEffect(() => {
    if (open) {
      setSelectedTreatments(new Set(treatments.map((t) => t.id)));
      setSelectedExams(new Set(examsOrProcedures.map((e) => e.id)));
      setSelectedReports(new Set(reports.map((r) => r.id)));
      setIncludeGuidelines(!!guidelines);
      setSelectedQuestionnaireIds(
        new Set(questionnaires.map((q) => q.questionnaireId)),
      );
      setError("");
    }
  }, [open, treatments, examsOrProcedures, reports, guidelines, questionnaires]);

  /**
   * Calcula o total de itens selecionados
   */
  const totalSelectedItems = useMemo(() => {
    const qCount = questionnaires.filter((q) =>
      selectedQuestionnaireIds.has(q.questionnaireId),
    ).length;
    return (
      selectedTreatments.size +
      selectedExams.size +
      selectedReports.size +
      (includeGuidelines ? 1 : 0) +
      qCount
    );
  }, [
    selectedTreatments,
    selectedExams,
    selectedReports,
    includeGuidelines,
    questionnaires,
    selectedQuestionnaireIds,
  ]);

  /**
   * Toggle de seleção de tratamento
   */
  const toggleTreatment = (id: string) => {
    setSelectedTreatments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  /**
   * Toggle de seleção de exame
   */
  const toggleExam = (id: string) => {
    setSelectedExams((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  /**
   * Toggle de seleção de relatório
   */
  const toggleReport = (id: string) => {
    setSelectedReports((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleQuestionnaire = (questionnaireId: string) => {
    setSelectedQuestionnaireIds((prev) => {
      const next = new Set(prev);
      if (next.has(questionnaireId)) next.delete(questionnaireId);
      else next.add(questionnaireId);
      return next;
    });
  };

  /**
   * Seleciona/deseleciona todos os itens de uma categoria
   */
  const toggleAllCategory = (
    category: "treatments" | "exams" | "reports",
    selectAll: boolean
  ) => {
    if (category === "treatments") {
      setSelectedTreatments(
        selectAll ? new Set(treatments.map((t) => t.id)) : new Set()
      );
    } else if (category === "exams") {
      setSelectedExams(
        selectAll ? new Set(examsOrProcedures.map((e) => e.id)) : new Set()
      );
    } else if (category === "reports") {
      setSelectedReports(
        selectAll ? new Set(reports.map((r) => r.id)) : new Set()
      );
    }
  };

  /**
   * Salva o protocolo
   */
  const handleSave = async () => {
    // Validação
    if (!templateName.trim()) {
      setError("Por favor, informe um nome para o protocolo");
      return;
    }

    if (totalSelectedItems === 0) {
      setError("Selecione pelo menos um item para incluir no protocolo");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      // Filtra apenas os itens selecionados (remove o campo 'id' pois é específico do atendimento)
      const selectedTreatmentsData = treatments
        .filter((t) => selectedTreatments.has(t.id))
        .map(({ id, ...rest }) => rest);

      const selectedExamsData = examsOrProcedures
        .filter((e) => selectedExams.has(e.id))
        .map(({ id, ...rest }) => rest);

      const selectedReportsData = reports
        .filter((r) => selectedReports.has(r.id))
        .map(({ id, ...rest }) => rest);

      const selectedQuestionnairesData = questionnaires.filter((q) =>
        selectedQuestionnaireIds.has(q.questionnaireId),
      );

      // Cria o protocolo (agora é assíncrono)
      await createTemplate({
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        treatments:
          selectedTreatmentsData.length > 0
            ? selectedTreatmentsData
            : undefined,
        examsOrProcedures:
          selectedExamsData.length > 0 ? selectedExamsData : undefined,
        reports:
          selectedReportsData.length > 0 ? selectedReportsData : undefined,
        guidelines: includeGuidelines && guidelines ? guidelines : undefined,
        questionnaires:
          selectedQuestionnairesData.length > 0
            ? selectedQuestionnairesData
            : undefined,
      });

      // Chama callback de sucesso se fornecido
      if (onSaveSuccess) {
        onSaveSuccess();
      }

      // Reseta o formulário e fecha
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Erro ao salvar protocolo. Tente novamente."
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Fecha o modal e reseta o estado
   */
  const handleClose = () => {
    setTemplateName("");
    setTemplateDescription("");
    setError("");
    setIsSaving(false);
    onClose();
  };

  // Verifica se há itens para salvar
  const hasItems =
    treatments.length > 0 ||
    examsOrProcedures.length > 0 ||
    reports.length > 0 ||
    !!guidelines ||
    questionnaires.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Save sx={{ color: "#00A884" }} />
          <Typography variant="h6" fontWeight={600}>
            Salvar como Protocolo
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Informações do protocolo */}
          <Box>
            <TextField
              fullWidth
              label="Nome do protocolo *"
              placeholder="Ex: Protocolo Dermatite Atópica"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              variant="outlined"
              size="small"
              autoFocus
            />
          </Box>

          <Box>
            <TextField
              fullWidth
              label="Descrição (opcional)"
              placeholder="Ex: Protocolo padrão para casos de dermatite atópica leve a moderada"
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              variant="outlined"
              size="small"
              multiline
              rows={2}
            />
          </Box>

          <Divider />

          {/* Seleção de itens */}
          {!hasItems ? (
            <Alert severity="info">
              Não há itens para salvar. Adicione tratamentos, exames, relatórios
              ou orientações antes de criar um protocolo.
            </Alert>
          ) : (
            <>
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Selecione os itens para incluir no protocolo:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Você pode escolher quais itens deseja salvar no protocolo
                </Typography>
              </Box>

              {/* Tratamentos */}
              {treatments.length > 0 && (
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#00A884"
                    >
                      💊 Tratamentos ({selectedTreatments.size}/
                      {treatments.length})
                    </Typography>
                  </Stack>
                  <Stack spacing={0.5} pl={1}>
                    {treatments.map((treatment) => (
                      <FormControlLabel
                        key={treatment.id}
                        control={
                          <Checkbox
                            checked={selectedTreatments.has(treatment.id)}
                            onChange={() => toggleTreatment(treatment.id)}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            <strong>{treatment.name}</strong> -{" "}
                            {treatment.dosage}
                          </Typography>
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Exames/Procedimentos */}
              {examsOrProcedures.length > 0 && (
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#00A884"
                    >
                      🔬 Exames/Procedimentos ({selectedExams.size}/
                      {examsOrProcedures.length})
                    </Typography>
                  </Stack>
                  <Stack spacing={0.5} pl={1}>
                    {examsOrProcedures.map((exam) => (
                      <FormControlLabel
                        key={exam.id}
                        control={
                          <Checkbox
                            checked={selectedExams.has(exam.id)}
                            onChange={() => toggleExam(exam.id)}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {exam.description}
                          </Typography>
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Relatórios */}
              {reports.length > 0 && (
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#00A884"
                    >
                      📄 Relatórios ({selectedReports.size}/{reports.length})
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Chip
                        label="Todos"
                        size="small"
                        onClick={() => toggleAllCategory("reports", true)}
                        sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                      />
                      <Chip
                        label="Nenhum"
                        size="small"
                        onClick={() => toggleAllCategory("reports", false)}
                        sx={{ cursor: "pointer", fontSize: "0.7rem" }}
                      />
                    </Stack>
                  </Stack>
                  <Stack spacing={0.5} pl={1}>
                    {reports.map((report) => (
                      <FormControlLabel
                        key={report.id}
                        control={
                          <Checkbox
                            checked={selectedReports.has(report.id)}
                            onChange={() => toggleReport(report.id)}
                            size="small"
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{ maxWidth: 500 }}
                          >
                            {report.content.substring(0, 80)}
                            {report.content.length > 80 ? "..." : ""}
                          </Typography>
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Questionários selecionados no atendimento */}
              {questionnaires.length > 0 && (
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="#00A884"
                    gutterBottom
                  >
                    📝 Questionários (
                    {
                      questionnaires.filter((q) =>
                        selectedQuestionnaireIds.has(q.questionnaireId),
                      ).length
                    }
                    /{questionnaires.length})
                  </Typography>
                  <Stack spacing={0.5} pl={1}>
                    {questionnaires.map((q) => (
                      <FormControlLabel
                        key={q.questionnaireId}
                        control={
                          <Checkbox
                            checked={selectedQuestionnaireIds.has(
                              q.questionnaireId,
                            )}
                            onChange={() => toggleQuestionnaire(q.questionnaireId)}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {q.title || q.name}
                          </Typography>
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Orientações */}
              {guidelines && (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeGuidelines}
                        onChange={(e) => setIncludeGuidelines(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="#00A884"
                      >
                        📋 Incluir Orientações
                      </Typography>
                    }
                  />
                </Box>
              )}

              {/* Resumo */}
              <Alert severity="info" icon={false}>
                <Typography variant="body2">
                  <strong>{totalSelectedItems}</strong>{" "}
                  {totalSelectedItems === 1
                    ? "item selecionado"
                    : "itens selecionados"}{" "}
                  para salvar no protocolo
                </Typography>
              </Alert>
            </>
          )}

          {/* Mensagem de erro */}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} startIcon={<Close />} disabled={isSaving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<Save />}
          disabled={isSaving || !hasItems}
          sx={{
            bgcolor: "#00A884",
            "&:hover": { bgcolor: "#008c6e" },
          }}
        >
          {isSaving ? "Salvando..." : "Salvar Protocolo"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateSaver;
